import { Scene } from "onearm";
import { Signal } from "typed-signals";
import gsap from "gsap";
import { ImageNumbers } from "../../components/ImageNumbers.js";
import { FrameFlash } from "../../components/FrameFlash.js";

const COUNT_DURATION = 2;
const REVEAL_AT = 0.7;

export class WinPopupScene extends Scene {
    onClosed = new Signal();

    create(options) {
        super.create(options);

        const resources = this.services.get("resources");

        // ImageNumbers в BigWinLabel.value
        const valueContainer = this.layout.find("BigWinLabel.value")
            ?? this.layout.find("value");

        if (valueContainer) {
            valueContainer.removeChildren();
            this.numbers = new ImageNumbers({ resources, size: 160 });
            this.numbers.setValue(0);
            valueContainer.addChild(this.numbers);
        }

        // GameZone — это весь видимый контент попапа ("Content"); стартует невидимым.
        // WinPopupBG лежит ВНУТРИ GameZone, поэтому его alpha не трогаем (иначе двойной фейд).
        // Ссылка на bg нужна только как fallback для позиционирования вспышки.
        this.bg = this.layout.find("WinPopupBG");
        this.gameZone = this.layout.find("GameZone");
        if (this.gameZone) this.gameZone.alpha = 0;

        // BigWinText: anchor 0.5 + сдвиг на половину размера, чтобы scale-pop шёл из центра.
        // Layout движка центрирует по top-left, игнорирует anchor и переприменяет позицию на
        // каждом resize — поэтому половину размера кладём и в display.offset (переживёт resize,
        // суммируется с centerX/centerY), и сразу в x/y (для первого кадра).
        this.bigWinText = this.layout.get("BigWinText");
        if (this.bigWinText?.anchor) {
            this.bigWinText.anchor.set(0.5);
            const halfW = this.bigWinText.width / 2;
            const halfH = this.bigWinText.height / 2;
            const display = this.bigWinText.display;
            if (display) {
                const off = display.offset ?? {};
                display.offset = { ...off, x: (off.x ?? 0) + halfW, y: (off.y ?? 0) + halfH };
            }
            this.bigWinText.x += halfW;
            this.bigWinText.y += halfH;
        }

        // Переходная вспышка живёт в AnimationContainer (он идёт после GameZone — рисуется
        // поверх контента), по центру экрана.
        this.flash = new FrameFlash({ resources, prefix: "BG_", count: 52, fps: 30 });
        this.flash.visible = false;
        const animationContainer = this.layout.find("AnimationContainer");
        (animationContainer ?? this).addChild(this.flash);

        const fullScreen = this.services.get("resizeSystem")?.getContext?.()?.zone?.fullScreen;
        if (fullScreen) {
            // Скейлим вспышку по высоте, чтобы она занимала всю высоту экрана.
            const frameH = this.flash.sprite?.texture?.height;
            this.flash.scale.set(frameH ? fullScreen.height / frameH : 4);
            this.flash.position.set(
                fullScreen.left + fullScreen.width / 2,
                fullScreen.top + fullScreen.height / 2,
            );
        } else {
            this.flash.scale.set(4);
            if (this.bg) {
                this.flash.position.set(
                    this.bg.x + this.bg.width / 2,
                    this.bg.y + this.bg.height / 2,
                );
            }
        }

        this.eventMode = "static";
        this.on("pointertap", () => this.onTap());

        const soundToggle = this.layout.find("SoundToggle");
        if (soundToggle?.onChange) {
            const audio = this.services.get("audio");
            soundToggle.onChange.connect((v) => audio.muteAll(!v));
        }
    }

    async show() {
        this.visible = true;
        await this.flash.warmup();
    }

    async showWin({ value }) {
        // Сброс состояния. alpha BigWinText не трогаем — он придёт от фейда GameZone (родителя).
        this.numbers?.setValue(0);
        this.numbers?.scale.set(1);
        this.bigWinText?.scale.set(0.9);
        if (this.gameZone) this.gameZone.alpha = 0;
        this.pulse?.kill();
        this.pulse = null;
        this.flash.reset();
        this.flash.visible = true;

        // Контент попапа — отдельный таймлайн, стартует на reveal вспышки.
        const content = gsap.timeline({ paused: true });

        // Шаг 2: Content (GameZone целиком) плавно проявляется под вспышкой.
        content.to(this.gameZone, {
            alpha: 1, duration: 0.3, ease: "sine.out",
        }, 0);

        if (this.numbers) {
            const counter = { val: 0 };

            content.to(counter, {
                val: value,
                duration: COUNT_DURATION,
                ease: "linear",
                onUpdate: () => this.numbers.setValue(counter.val),
            }, 0);

            content.to(this.numbers.scale, {
                x: 1.2, y: 1.2,
                duration: COUNT_DURATION,
                ease: "power1.in",
            }, 0);

            content.to(this.numbers.scale, {
                x: 1.0, y: 1.0,
                duration: 0.35,
                ease: "back.out(3)",
            }, COUNT_DURATION);
        }

        // Шаг 3: BigWinText появляется вместе с GameZone (alpha от родителя) и делает
        // лёгкий «punch» из 0.9 → 1, затем — мягкая пульсация.
        if (this.bigWinText) {
            content.to(this.bigWinText.scale, {
                x: 1, y: 1,
                duration: 0.5, ease: "back.out(2)",
                onComplete: () => this.startPulse(),
            }, 0);
        }

        const contentDone = new Promise((resolve) => {
            content.eventCallback("onComplete", resolve);
        });

        // Вспышка играет полностью; на 70% запускает контент.
        await this.flash.play({ revealAt: REVEAL_AT, onReveal: () => content.play() });
        // Ждём именно контент (счётчик длиннее вспышки) — иначе попап закроют посреди счёта.
        await contentDone;

        this.numbers?.setValue(value);

        // Ждём тап
        await new Promise((r) => { this.tapResolve = r; });

        // Остановить пульсацию до удаления сцены (repeat:-1 твин иначе утечёт)
        this.pulse?.kill();
        this.pulse = null;
        if (this.bigWinText) this.bigWinText.scale.set(1);

        // Fade-out
        await gsap.to(this, { alpha: 0, duration: 0.3, ease: "sine.in" }).then();
        this.onClosed.emit();
    }

    startPulse() {
        this.pulse?.kill();
        this.pulse = gsap.to(this.bigWinText.scale, {
            x: 1.06, y: 1.06,
            duration: 0.7, ease: "sine.inOut",
            yoyo: true, repeat: -1,
        });
    }

    onTap() {
        if (this.tapResolve) {
            this.tapResolve();
            this.tapResolve = null;
        }
    }
}
